import { ofType } from 'redux-observable';
import { List } from 'immutable';
import { of } from 'rxjs';
import { mergeMap, flatMap, catchError, map } from 'rxjs/operators';
import i18next from 'i18next';
import { combineEpics } from 'web-app/util/redux-observable';
// To support APIs where other request methods than the expected ones are used.
// e.g. a delete api uses POST
const getRestFunction = (method, RESTClient) => {
    switch (method) {
        case 'DELETE':
            return RESTClient.deleteAuthenticatedObservable;
        case 'POST':
            return RESTClient.postAuthenticatedObservable;
        case 'GET':
            return RESTClient.getAuthenticatedObservable;
        default:
            throw new Error(`Uknown type ${method} given to getRestFunction in epics factory`);
    }
};
const getRequestMethodFromEndpointDescription = (endpointDescription, defaultMethod, payload, RESTClient) => {
    const { requestMethod } = endpointDescription;
    const methodFromDescription = typeof requestMethod === 'function' ? requestMethod(payload) : requestMethod;
    return getRestFunction(methodFromDescription || defaultMethod, RESTClient);
};
const getUrl = (requestTypeDescription, payload) => {
    const url = requestTypeDescription.url;
    if (typeof url === 'function') {
        return url(payload);
    }
    else if (typeof url === 'string') {
        return url;
    }
    else {
        throw Error('url must be a function or a string');
    }
};
const getRequestPayload = (requestTypeDescription, payload) => {
    const requestPayload = requestTypeDescription.requestPayload;
    if (typeof requestPayload === 'function') {
        return requestPayload(payload);
    }
    else if (!requestPayload) {
        return payload;
    }
    else {
        throw Error('requestPayload must be a function or empty');
    }
};
const locateResponse = (requestTypeDescription, response) => {
    if (typeof requestTypeDescription.locateResponse === 'function') {
        return requestTypeDescription.locateResponse(response);
    }
    else {
        return response;
    }
};
const getEntityReferences = (requestKey, response, entityRelations, originalPayload) => {
    return entityRelations
        .filter(relation => typeof relation.responseHandlers[requestKey] === 'function')
        .map(relation => {
        const responseItems = relation.responseHandlers[requestKey](response, originalPayload);
        const items = List(responseItems).map(relation.entity.model.fromAPI);
        return relation.entity.actions.internalActions.save.action(items);
    });
};
const epicFromObservable = (observable, actionCollection) => {
    return action$ => action$.pipe(ofType(actionCollection.start.type), mergeMap(({ payload }) => {
        return observable(payload).pipe(flatMap(a => a), catchError((e) => of(actionCollection.failed.action(e, payload))));
    }));
};
export const createEpics = (apiDescription, actions, entityModel, entityName, entityRelations, onEntityInstanceCreated, onEntityInstanceDeleted, RESTClient, handleDeleteConfirm) => {
    const observablesObject = {};
    const epicsArray = [];
    if (apiDescription.create) {
        const endpointDescription = apiDescription.create;
        const createObservable = (payload) => {
            const url = getUrl(endpointDescription, payload);
            const requestPayload = getRequestPayload(endpointDescription, payload);
            return RESTClient.postAuthenticatedObservable(url, requestPayload).pipe(map((response) => entityModel.fromAPI(locateResponse(endpointDescription, response), requestPayload)), map(response => {
                onEntityInstanceCreated(entityName);
                return [actions.create.success.action(response, payload)];
            }));
        };
        const createEpic = epicFromObservable(createObservable, actions.create);
        observablesObject.create = createObservable;
        epicsArray.push(createEpic);
    }
    if (apiDescription.fetch) {
        const endpointDescription = apiDescription.fetch;
        const readObservable = (payload) => {
            const url = getUrl(endpointDescription, payload);
            const requestPayload = getRequestPayload(endpointDescription, payload);
            return RESTClient.getAuthenticatedObservable(url, requestPayload).pipe(map(response => entityModel.fromAPI(locateResponse(endpointDescription, response), requestPayload)), map(response => [actions.fetch.success.action(response, payload)]));
        };
        const epic = epicFromObservable(readObservable, actions.fetch);
        observablesObject.fetch = readObservable;
        epicsArray.push(epic);
    }
    if (apiDescription.fetchAll) {
        const endpointDescription = apiDescription.fetchAll;
        const readAllObservable = (payload) => {
            const url = getUrl(endpointDescription, payload);
            const requestPayload = getRequestPayload(endpointDescription, payload);
            const defaultRequestMethod = 'GET';
            const RESTFunction = getRequestMethodFromEndpointDescription(endpointDescription, defaultRequestMethod, payload, RESTClient);
            return RESTFunction(url, requestPayload).pipe(map(response => {
                return {
                    items: List(locateResponse(endpointDescription, response)).map(res => entityModel.fromAPI(res, requestPayload)),
                    entityReferences: getEntityReferences('fetchAll', response, entityRelations, payload),
                };
            }), map(response => {
                return [...response.entityReferences, actions.fetchAll.success.action(response.items, payload)];
            }));
        };
        const epic = epicFromObservable(readAllObservable, actions.fetchAll);
        observablesObject.fetchAll = readAllObservable;
        epicsArray.push(epic);
    }
    if (apiDescription.update) {
        const endpointDescription = apiDescription.update;
        const updateObservable = (payload) => {
            const url = getUrl(endpointDescription, payload);
            const requestPayload = getRequestPayload(endpointDescription, payload);
            return RESTClient.postAuthenticatedObservable(url, requestPayload).pipe(map(response => [
                actions.update.success.action(entityModel.fromAPI(locateResponse(endpointDescription, response), requestPayload), payload),
            ]));
        };
        const epic = epicFromObservable(updateObservable, actions.update);
        observablesObject.update = updateObservable;
        epicsArray.push(epic);
    }
    if (apiDescription.delete) {
        const endpointDescription = apiDescription.delete;
        const defaultRequestMethod = 'DELETE';
        const deleteObservable = (payload) => {
            const RESTFunction = getRequestMethodFromEndpointDescription(endpointDescription, defaultRequestMethod, payload, RESTClient);
            const url = getUrl(endpointDescription, payload);
            const requestPayload = getRequestPayload(endpointDescription, payload);
            return handleDeleteConfirm(i18next.t($ => $.entities.deleteConfirm, {
                entityName,
            })).pipe(flatMap(selection => {
                if (selection === 1) {
                    return RESTFunction(url, requestPayload).pipe(map(response => {
                        onEntityInstanceDeleted(entityName);
                        return [actions.delete.success.action(response, payload)];
                    }));
                }
                return of([actions.delete.aborted.action(payload)]);
            }));
        };
        const epic = epicFromObservable(deleteObservable, actions.delete);
        observablesObject.delete = deleteObservable;
        epicsArray.push(epic);
    }
    const epics = combineEpics(...epicsArray);
    return { epics, observables: observablesObject };
};
